<template>
  <Teleport to="body">
    <cl-modal
      :headerLabel="$t('Add an MSP')"
      :visible="showModal"
      @on-close="onCloseModal"
    >
      <div class="tw-mb-4 tw-rounded-md tw-bg-light tw-p-3 sm:tw-col-span-2">
        <cl-stepper :steps="steps" />
      </div>
      <!-- First step - create MSP -->
      <form @submit.prevent="submit" autocomplete="off" v-if="isFirstStep">
        <div class="tw-grid sm:tw-grid-cols-2 sm:tw-gap-4">
          <div>
            <cl-form-group>
              <cl-form-label label-for="msp_name">
                {{ $t("Name") }}
              </cl-form-label>
              <cl-form-input
                data-lpignore="true"
                id="msp_name"
                name="msp_name"
                v-model:value="createMspForm.name"
                :placeholder="$t('MSP Name')"
                :state="setInputState(v$.createMspForm.name)"
                @on-blur="v$.createMspForm.name.$touch"
              />
              <div
                class="tw-block tw-text-sm tw-text-danger-500"
                v-if="v$.createMspForm.name.$error"
              >
                {{ $t("The name must be at least 2 characters long") }}
              </div>
            </cl-form-group>
          </div>
          <div>
            <cl-form-group>
              <cl-form-label label-for="description">
                {{ this.$t("Description (optional)") }}
              </cl-form-label>
              <cl-form-input
                id="description"
                v-model:value="createMspForm.description"
                :placeholder="$t('Description')"
              />
            </cl-form-group>
          </div>
        </div>
      </form>
      <!-- Second step - create MSP Admin -->
      <form @submit.prevent="submit" autocomplete="off" v-else>
        <div class="tw-grid sm:tw-grid-cols-2 sm:tw-gap-4">
          <div>
            <cl-form-group>
              <cl-form-label label-for="first_name">
                {{ $t("First name") }}
              </cl-form-label>
              <cl-form-input
                data-lpignore="true"
                id="first_name"
                name="first_name"
                v-model:value="createAdminForm.first_name"
                :placeholder="$t('First name')"
              />
            </cl-form-group>
          </div>
          <div>
            <cl-form-group>
              <cl-form-label label-for="last_name">
                {{ $t("Last name") }}
              </cl-form-label>
              <cl-form-input
                data-lpignore="true"
                id="last_name"
                name="last_name"
                v-model:value="createAdminForm.last_name"
                :placeholder="$t('Last name')"
              />
            </cl-form-group>
          </div>
          <div>
            <cl-form-group>
              <cl-form-label label-for="email">
                {{ $t("Email") }}
              </cl-form-label>
              <cl-form-input
                id="email"
                v-model:value="createAdminForm.email"
                placeholder="e.g. name@company.com"
                :state="setInputState(v$.createAdminForm.email)"
                @on-blur="v$.createAdminForm.email.$touch"
              />
              <div
                class="tw-block tw-text-sm tw-text-danger-500"
                v-if="v$.createAdminForm.email.$error"
              >
                {{ $t("Please enter a valid email") }}
              </div>
            </cl-form-group>
          </div>
          <div
            class="tw-flex tw-items-center tw-justify-between"
            v-if="hasLinkLockPermission"
          >
            <cl-form-label>
              {{ $t("Link Lock Admin") }}
            </cl-form-label>
            <cl-form-radio-group
              :selectedValue="createAdminForm.linklock"
              @update:selected="
                (selected) => (createAdminForm.linklock = selected)
              "
              switcher
              button
              :options="radioOptions"
            />
          </div>
          <div>
            <cl-form-group>
              <cl-form-label label-for="password">
                {{ $t("Password") }}
              </cl-form-label>
              <cl-form-input
                id="password"
                type="password"
                :state="setInputState(v$.createAdminForm.password)"
                v-model:value="createAdminForm.password"
                @on-blur="v$.createAdminForm.password.$touch"
              />
              <div
                class="tw-block tw-text-sm tw-text-danger-500"
                v-if="v$.createAdminForm.password.$error"
              >
                {{
                  $t(
                    "The password must contain at least one uppercase, one lowercase letter, one symbol must be at least 12 characters"
                  )
                }}
              </div>
            </cl-form-group>
          </div>
          <div>
            <cl-form-group>
              <cl-form-label label-for="confirm_password">
                {{ $t("Confirm Password") }}
              </cl-form-label>
              <cl-form-input
                id="confirm_password"
                type="password"
                :state="setInputState(createAdminForm.confirm_password)"
                v-model:value="createAdminForm.confirm_password"
                @on-blur="v$.createAdminForm.confirm_password.$touch"
              />
              <div
                class="tw-block tw-text-sm tw-text-danger-500"
                v-if="v$.createAdminForm.confirm_password.$error"
              >
                {{
                  $t(
                    "The Password and the Confirm Password fields should match"
                  )
                }}
              </div>
            </cl-form-group>
          </div>
        </div>
      </form>
      <template v-slot:[`footer.decline`]>
        <cl-button
          :disabled="fetching"
          @on-click="onCloseModal"
          data-test-id="modal-msp-cancel-button"
          variant="link-secondary"
        >
          <span>{{ this.$t("Cancel") }}</span>
        </cl-button>
      </template>
      <template v-slot:[`footer.accept`]>
        <cl-button
          :variant="'secondary'"
          @on-click="submit"
          data-test-id="modal-msp-add-button"
        >
          <span v-if="!fetching">{{ this.$t("Add") }}</span>
          <cl-spinner v-else :size="'small'" />
        </cl-button>
      </template>
    </cl-modal>
  </Teleport>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, email, sameAs } from "@vuelidate/validators";
import { TIER, STRONG_PASSWORD, STEPS } from "@/constants";
import checkPermission from "@/utils/check-permission";
import { useFocusElement } from "@/composables/useFocusElement";

const DEFAULT_CREATE_MSP_FORM = Object.freeze({
  name: "",
  description: "",
});

const DEFAULT_CREATE_ADMIN_FORM = Object.freeze({
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  confirm_password: "",
  linklock: false,
});

export default {
  setup() {
    const { focusElementById } = useFocusElement();
    return { v$: useVuelidate(), focusElementById };
  },
  data() {
    return {
      fetching: false,
      step: STEPS.first,
      tierType: TIER.msps,
      hasLinkLockPermission: false,
      mspId: "",
      createMspForm: {
        ...DEFAULT_CREATE_MSP_FORM,
      },
      createAdminForm: {
        ...DEFAULT_CREATE_ADMIN_FORM,
      },
      radioOptions: [
        { label: this.$t("Yes"), value: true },
        { label: this.$t("No"), value: false },
      ],
    };
  },
  validations() {
    return {
      createMspForm: {
        name: {
          minLengthValue: minLength(2),
          required,
        },
      },
      createAdminForm: {
        email: {
          email,
          required,
        },
        password: {
          strongPassword: (password) => STRONG_PASSWORD.test(password),
          required,
        },
        confirm_password: {
          sameAsPassword: sameAs(this.createAdminForm.password),
          required,
        },
      },
    };
  },
  computed: {
    ...mapGetters("authentication", ["permissions"]),
    ...mapGetters("modal", ["showModal"]),
    isFirstStep() {
      return this.step === STEPS.first;
    },
    steps() {
      return [
        {
          index: 1,
          label: this.$t("Add an MSP"),
          current: this.isFirstStep,
          complited: !this.isFirstStep,
          actionable: false,
        },
        {
          index: 2,
          label: this.$t("Add Administrator"),
          current: !this.isFirstStep,
          complited: false,
          actionable: false,
        },
      ];
    },
  },
  watch: {
    showModal: {
      handler() {
        this.focusElementById("msp_name");
      },
      deep: true,
      immediate: true,
    },
    isFirstStep: {
      handler() {
        this.focusElementById("first_name");
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("msps", ["createMsp"]),
    ...mapActions("administrators", ["createAdministrator"]),
    ...mapActions("modal", ["closeModal"]),
    setInputState(input) {
      return input.$dirty ? !input.$error : null;
    },
    onCloseModal() {
      this.closeModal();
      this.v$.$reset();
      this.step = STEPS.first;
      this.createMspForm = { ...DEFAULT_CREATE_MSP_FORM };
      this.createAdminForm = { ...DEFAULT_CREATE_ADMIN_FORM };
    },
    async submit() {
      if (this.step === STEPS.first) {
        const isValid = await this.v$.createMspForm.$validate();
        if (!isValid) return;

        try {
          this.fetching = true;

          const response = await this.createMsp({
            formData: this.createMspForm,
          });

          if (response.status === 200 || response.status === 201) {
            this.displayToast({
              title: this.$t("Success"),
              message: this.$t("MSP has been created"),
              duration: 2000,
              variant: "success",
            });
          }

          this.mspId = response.data.id;
          this.step = STEPS.second;
        } catch (_err) {
          // stub
        } finally {
          this.fetching = false;
        }

        return;
      }

      if (this.step === STEPS.second) {
        const isValid = await this.v$.createAdminForm.$validate();
        if (!isValid) return;

        try {
          this.fetching = true;

          const response = await this.createAdministrator({
            tier: this.tierType,
            id: this.mspId,
            formData: this.createAdminForm,
          });

          if (response.status === 200 || response.status === 201) {
            this.displayToast({
              title: this.$t("Success"),
              message: this.$t("MSP Admin has been created"),
              duration: 2000,
              variant: "success",
            });
          }
        } catch (_err) {
          // stub
        } finally {
          this.fetching = false;
          this.onCloseModal();
        }
      }
    },
  },
  created() {
    this.hasLinkLockPermission = checkPermission(this.permissions, "link-lock");
  },
};
</script>
